const parseCoordinate = value => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim())

  return Number.isFinite(parsed) ? parsed : null
}

const isValidLatitude = latitude => latitude >= -90 && latitude <= 90

const isValidLongitude = longitude => longitude >= -180 && longitude <= 180

const isValidCoordinatePair = (latitude, longitude) => (
  latitude !== null &&
  longitude !== null &&
  isValidLatitude(latitude) &&
  isValidLongitude(longitude)
)

/**
 * Normalizes geocodedAddress from API (object or JSON string).
 * @param geocodedAddress {object|string|null|undefined}
 * @returns {object|null}
 */
export const normalizeGeocodedAddress = geocodedAddress => {
  if (!geocodedAddress) {
    return null
  }

  if (typeof geocodedAddress === 'object') {
    return geocodedAddress
  }

  if (typeof geocodedAddress === 'string') {
    try {
      const parsed = JSON.parse(geocodedAddress)
      return typeof parsed === 'object' && parsed !== null ? parsed : null
    } catch (e) {
      return null
    }
  }

  return null
}

/**
 * Extracts coordinates from common geolocation payload shapes (ip-api, ipinfo, etc.).
 * @param geocodedAddress {object|string|null|undefined}
 * @returns {{ latitude: number, longitude: number, geocodedAddress: object }|null}
 */
export const extractCoordinatesFromGeocodedAddress = geocodedAddress => {
  const normalized = normalizeGeocodedAddress(geocodedAddress)

  if (!normalized) {
    return null
  }

  const explicitLatitude = parseCoordinate(
    normalized.lat ?? normalized.latitude ?? normalized.y
  )
  const explicitLongitude = parseCoordinate(
    normalized.lng ?? normalized.lon ?? normalized.longitude ?? normalized.x
  )

  if (isValidCoordinatePair(explicitLatitude, explicitLongitude)) {
    return {
      latitude: explicitLatitude,
      longitude: explicitLongitude,
      geocodedAddress: normalized
    }
  }

  if (typeof normalized.loc === 'string' && normalized.loc.trim()) {
    const [latPart, lngPart] = normalized.loc.split(',').map(part => part.trim())
    const locLatitude = parseCoordinate(latPart)
    const locLongitude = parseCoordinate(lngPart)

    if (isValidCoordinatePair(locLatitude, locLongitude)) {
      return {
        latitude: locLatitude,
        longitude: locLongitude,
        geocodedAddress: normalized
      }
    }
  }

  return null
}

/**
 * @param click {{ latitude?: number, longitude?: number, geocodedAddress?: * }}
 * @returns {boolean}
 */
export const clickHasMapCoordinates = click => (
  isValidCoordinatePair(
    parseCoordinate(click?.latitude),
    parseCoordinate(click?.longitude)
  )
)

/**
 * @param geocodedAddress {object|string|null|undefined}
 * @returns {string}
 */
/**
 * @param clicks {Array<{ latitude?: number, longitude?: number }>}
 * @returns {{ lat: number, lng: number }[]}
 */
export const getMapPositionsFromClicks = clicks => (clicks || [])
  .filter(clickHasMapCoordinates)
  .map(click => ({
    lat: click.latitude,
    lng: click.longitude
  }))

export const formatGeocodedAddressLabel = geocodedAddress => {
  const normalized = normalizeGeocodedAddress(geocodedAddress)

  if (!normalized) {
    return typeof geocodedAddress === 'string' && geocodedAddress
      ? geocodedAddress
      : '?'
  }

  const parts = [
    normalized.postal,
    normalized.city,
    normalized.region,
    normalized.country
  ].filter(Boolean)

  const label = parts.join(', ')

  if (label && normalized.loc) {
    return `${label} (${normalized.loc})`
  }

  return label || normalized.loc || '?'
}
